import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { WorkerActivityDTO } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { DetailHeader, EmptyState, Panel, SectionHeader, toneColor } from "@/design/primitives";
import { ActivityTimeline, CeilingBars, seriesTone } from "./ActivityCharts";

const hours = (minutes: number) => `${(minutes / 60).toFixed(1).replace(".", ",")} h`;
const pct = (v: number | null, digits = 0) =>
  v == null ? "—" : `${v.toFixed(digits).replace(".", ",")} %`;

/**
 * Porovnání strojů (p3c).
 *
 * Tři pohledy, každý odpovídá na jinou otázku:
 * 1. hodinová osa — KDY který stroj běžel a kdy stál;
 * 2. strop tarifu — KOLIK by vůbec uvezl, kdyby měl práce dost;
 * 3. tabulka — spotřeba limitu na tisíc kroků, což je jediné číslo
 *    srovnatelné napříč stroji (nezávisí na rychlosti ani délce sessions).
 *
 * Bez trojice to svádí ke špatnému závěru: stroj s nízkou obsazeností může
 * stát kvůli prázdné frontě i kvůli levnějšímu tarifu a z prvního grafu se
 * to nepozná.
 */
export function WorkerComparePage() {
  const navigate = useNavigate();
  const activity = useQuery({
    queryKey: ["workers-activity", 7],
    queryFn: () => api.workersActivity(7),
    refetchInterval: 60_000,
  });

  if (activity.isLoading) {
    return (
      <div className="af-animate-fade text-[12px]" style={{ color: "var(--text-tertiary)" }}>
        {cs.common.loading}
      </div>
    );
  }

  const all = activity.data?.workers ?? [];
  const rows = all.filter((w) => w.enabled);

  if (!activity.data || rows.length === 0) {
    return (
      <div className="af-animate-fade">
        <DetailHeader back={<Link to="/workers">← {cs.workers.title}</Link>} title={cs.workers.compareTitle} />
        <Panel className="p-8">
          <EmptyState title={cs.workers.compareEmpty} note={cs.workers.compareEmptyNote} />
        </Panel>
      </div>
    );
  }

  const toneOf = (w: WorkerActivityDTO) => seriesTone(all.findIndex((x) => x.workerId === w.workerId));
  const ceilingRows = rows
    .filter((w) => w.capacity?.ceilingHoursPerWeek != null)
    .map((w) => ({
      id: w.workerId,
      name: w.name,
      tone: toneOf(w),
      ceilingHours: w.capacity!.ceilingHoursPerWeek!,
      note: w.tariff ?? undefined,
    }));

  return (
    <div className="af-animate-fade">
      <DetailHeader
        back={<Link to="/workers">← {cs.workers.title}</Link>}
        title={cs.workers.compareTitle}
        meta={
          <span className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>
            {cs.workers.compareWindow(activity.data.windowDays)}
          </span>
        }
      />

      <Panel className="mb-4 p-4">
        <SectionHeader label={cs.workers.activityTimeline} sub={cs.workers.compareTimelineNote} />
        <div className="mt-3">
          <ActivityTimeline
            startAt={activity.data.hourlyStartAt}
            series={rows.map((w) => ({
              id: w.workerId,
              name: w.name,
              tone: toneOf(w),
              hourly: w.hourly,
              note: w.tariff ?? undefined,
            }))}
          />
        </div>
      </Panel>

      {ceilingRows.length > 0 ? (
        <Panel className="mb-4 p-4">
          <SectionHeader label={cs.workers.compareCeiling} sub={cs.workers.compareCeilingNote} tone="violet" />
          <div className="mt-3">
            <CeilingBars rows={ceilingRows} />
          </div>
        </Panel>
      ) : null}

      <Panel className="p-4">
        <SectionHeader label={cs.workers.compareTable} sub={cs.workers.compareTableNote} tone="amber" />
        <div className="mt-3 overflow-x-auto">
          <table className="w-full border-collapse text-[12px]" style={{ minWidth: 620 }}>
            <thead>
              <tr>
                <Th align="left">{cs.workers.compareMetric}</Th>
                {rows.map((w) => (
                  <Th key={w.workerId}>
                    <button
                      type="button"
                      className="cursor-pointer"
                      style={{ color: toneColor[toneOf(w)] }}
                      onClick={() => void navigate({ to: "/workers/$id", params: { id: w.workerId } })}
                    >
                      {w.name}
                    </button>
                    <span className="block font-normal" style={{ color: "var(--text-tertiary)" }}>
                      {w.tariff ?? "—"}
                    </span>
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              <Tr label={cs.workers.activityUtilization} rows={rows} value={(w) => `${Math.round(w.totals.utilizationPct)} %`} />
              <Tr label={cs.workers.compareWorked} rows={rows} value={(w) => hours(w.totals.workedMinutes)} />
              <Tr
                label={cs.workers.compareIdle}
                rows={rows}
                value={(w) => hours(Math.max(0, w.totals.windowMinutes - w.totals.workedMinutes))}
              />
              <Tr label={cs.workers.activitySessions} rows={rows} value={(w) => String(w.totals.sessions)} />
              <Tr label={cs.workers.activityTasksDone} rows={rows} value={(w) => String(w.totals.tasksDone)} />
              <Tr label={cs.workers.compareTurns} rows={rows} value={(w) => w.totals.turns.toLocaleString("cs-CZ")} />
              <Tr
                label={cs.workers.activityAvgSession}
                rows={rows}
                value={(w) => `${w.totals.avgSessionMinutes.toFixed(1).replace(".", ",")} min`}
              />
              <Tr divider label={cs.workers.activityPer1000} rows={rows} value={(w) => pct(w.capacity?.weekPctPer1000Turns ?? null, 2)} />
              <Tr label={cs.workers.comparePer1000Session} rows={rows} value={(w) => pct(w.capacity?.sessionPctPer1000Turns ?? null, 1)} />
              <Tr
                label={cs.workers.activityCeiling}
                rows={rows}
                value={(w) =>
                  w.capacity?.ceilingHoursPerWeek != null ? `${Math.round(w.capacity.ceilingHoursPerWeek)} h` : "—"
                }
              />
              <Tr
                label={cs.workers.compareCeilingUtil}
                rows={rows}
                value={(w) =>
                  w.capacity?.ceilingUtilizationPct != null
                    ? `${Math.round(w.capacity.ceilingUtilizationPct)} %`
                    : "—"
                }
              />
              <Tr
                divider
                label={cs.workers.comparePrice}
                rows={rows}
                value={(w) => (w.monthlyPrice != null ? `${w.monthlyPrice.toLocaleString("cs-CZ")} ${w.currency}` : "—")}
              />
              <Tr
                label={cs.workers.comparePricePerHour}
                rows={rows}
                value={(w) => {
                  // Měsíční paušál na worker-hodinu stropu: cena / (strop × 4,33 týdne).
                  const ceiling = w.capacity?.ceilingHoursPerWeek;
                  if (w.monthlyPrice == null || ceiling == null || ceiling <= 0) return "—";
                  return `${(w.monthlyPrice / (ceiling * 4.33)).toFixed(2).replace(".", ",")} ${w.currency}`;
                }}
              />
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-[11px] leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
          {cs.workers.compareCaveat}
        </p>
      </Panel>
    </div>
  );
}

function Th({ children, align = "right" }: { children: React.ReactNode; align?: "left" | "right" }) {
  return (
    <th
      className="af-label px-3 py-2 font-semibold"
      style={{
        textAlign: align,
        color: "var(--text-primary)",
        borderBottom: "1px solid var(--border-default)",
      }}
    >
      {children}
    </th>
  );
}

function Tr({
  label,
  rows,
  value,
  divider,
}: {
  label: string;
  rows: WorkerActivityDTO[];
  value: (w: WorkerActivityDTO) => string;
  divider?: boolean;
}) {
  return (
    <tr style={divider ? { borderTop: "1px solid var(--border-default)" } : undefined}>
      <td
        className="px-3 py-2"
        style={{ color: "var(--text-secondary)", borderBottom: "1px solid var(--border-subtle)" }}
      >
        {label}
      </td>
      {rows.map((w) => (
        <td
          key={w.workerId}
          className="af-display px-3 py-2 text-right font-semibold"
          style={{ color: "var(--text-primary)", borderBottom: "1px solid var(--border-subtle)" }}
        >
          {value(w)}
        </td>
      ))}
    </tr>
  );
}
